import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

/** One release: a short commit hash and when it was deployed, either unknown. */
export interface ReleaseVersion {
  hash: string | null;
  date: string | null;
}

/** The footer's version line, ready to show. */
export interface ReleaseVersionLine {
  hashes: string;
  dates: string;
}

const UNKNOWN: ReleaseVersion = { hash: null, date: null };

/**
 * Which releases of the frontend and the backend this page runs on, for the footer's version
 * line.
 *
 * The frontend's comes from two tags the deployment workflow writes into `index.html`
 * (`release` and `release-date`), so it needs no request; a local build has neither. The
 * backend's comes from `api/v1/version`, which answers nulls when its release has no stamp; a
 * failed request counts as unknown too.
 */
@Injectable({ providedIn: 'root' })
export class ReleaseVersionService {
  private http = inject(HttpClient);
  private document = inject(DOCUMENT);

  readonly frontend: ReleaseVersion = {
    hash: this.meta('release'),
    date: this.meta('release-date'),
  };

  readonly backend$: Observable<ReleaseVersion> = this.http.get<ReleaseVersion>('api/v1/version').pipe(
    catchError(() => of(UNKNOWN)),
    shareReplay(1),
  );

  private meta(name: string): string | null {
    return this.document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content || null;
  }
}

/**
 * `a1b2c3d – e4f5a6b` and `18 October 2026 / 17 October 2026`, frontend first, with the dates
 * in the reader's language; a part not known is left out. Nothing when neither hash is known.
 */
export function releaseVersionLine(frontend: ReleaseVersion, backend: ReleaseVersion | null, language: string): ReleaseVersionLine | null {
  const releases = [frontend, backend ?? UNKNOWN];
  const hashes = releases.map((release) => release.hash).filter((hash): hash is string => !!hash);

  if (hashes.length === 0) {
    return null;
  }

  const dates = releases
    .map((release) => formatDate(release.date, language))
    .filter((date): date is string => !!date);

  return { hashes: hashes.join(' – '), dates: dates.join(' / ') };
}

function formatDate(date: string | null, language: string): string | null {
  const time = date ? Date.parse(date) : NaN;

  if (isNaN(time)) {
    return null;
  }

  // The same as dateStyle 'long', which the older TypeScript libraries of some apps do not know
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };

  try {
    return new Intl.DateTimeFormat(language, options).format(time);
  } catch {
    return new Intl.DateTimeFormat('en', options).format(time);
  }
}
